/**
 * Response classification for the /embed/v2/:embedId/async-data endpoint.
 *
 * While the API is building the data cache it replies `202 Accepted` with a
 * `{ status: 'building', dataType: '…' }` body instead of a data envelope.
 * The legacy API-served scripts (sessionboard-web-api services/embed/scripts/*)
 * poll again after 4s on a 202; the v0 widget bundles must do the same instead
 * of rendering the body as (empty) data. (SB-8666)
 */

/** Delay between polls while the API reports it is still building. */
export const BUILDING_RETRY_DELAY_MS = 4000;

/** Max scheduled retries before giving up (~60s at the default delay). */
export const MAX_BUILDING_RETRIES = 15;

/**
 * True when the response is a "still building" signal rather than data.
 * Checks both the HTTP status and the body shape, since either alone
 * can be lost through proxies/caches.
 *
 * @param {number} status HTTP status code
 * @param {*} body Parsed JSON body (may be null/undefined)
 * @returns {boolean}
 */
export const isBuildingResponse = (status, body) =>
  status === 202 || (body != null && typeof body === 'object' && body.status === 'building');

/**
 * Resolve the delay before the next poll, honoring a Retry-After header
 * (delta-seconds or HTTP-date form) when present and valid.
 *
 * @param {string|null} [retryAfterHeader] Raw Retry-After header value
 * @param {() => number} [now] Clock, injectable for tests
 * @returns {number} Delay in milliseconds
 */
export const getBuildingRetryDelayMs = (retryAfterHeader, now = Date.now) => {
  if (retryAfterHeader) {
    // Purely numeric values are delta-seconds; never fed to Date.parse,
    // which would misread e.g. '-3' as a year.
    if (/^\s*-?\d+(\.\d+)?\s*$/.test(retryAfterHeader)) {
      const seconds = Number(retryAfterHeader);
      if (Number.isFinite(seconds) && seconds >= 0) {
        return seconds * 1000;
      }
    } else {
      const dateMs = Date.parse(retryAfterHeader);
      if (!Number.isNaN(dateMs)) {
        return Math.max(0, dateMs - now());
      }
    }
  }
  return BUILDING_RETRY_DELAY_MS;
};

/**
 * Decide what to do with an async-data response.
 *
 * @param {Object} params
 * @param {number} params.status HTTP status code
 * @param {boolean} params.ok fetch Response.ok
 * @param {*} params.body Parsed JSON body
 * @param {number} params.attempt Zero-based count of retries already scheduled
 * @param {string|null} [params.retryAfter] Raw Retry-After header value
 * @param {number} [params.maxAttempts]
 * @returns {{action: 'data', data: *} | {action: 'retry', delayMs: number} | {action: 'error', message: string}}
 */
export const classifyAsyncDataResponse = ({
  status,
  ok,
  body,
  attempt,
  retryAfter = null,
  maxAttempts = MAX_BUILDING_RETRIES,
}) => {
  if (isBuildingResponse(status, body)) {
    if (attempt >= maxAttempts) {
      return {
        action: 'error',
        message: 'Data is still being prepared. Please refresh the page in a moment.',
      };
    }
    return { action: 'retry', delayMs: getBuildingRetryDelayMs(retryAfter) };
  }

  if (!ok) {
    return {
      action: 'error',
      message: (body && body.error) || `HTTP error! status: ${status}`,
    };
  }

  return { action: 'data', data: body };
};
